import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { find, isNil, isUndefined, map as lodashMap } from 'lodash-es';
import { map, Observable, of, tap } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { AddressEnum } from '../../../../../enums/address';
import * as fromWalkingFarmActions from '../../../../../store/actions/walking-farm.actions';
import * as fromWalkingFarmSelectors from '../../../../../store/selectors/walking-farm.selectors';
import { WalkingFarmAlert, WalkingFarmProperty, WalkingFarmUnreadAlert } from '../../../../../typings/walking-farm';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-property-alerts',
  standalone: false,
  templateUrl: './alerts.component.html'
})
export class AlertsPageComponent {
  private activatedRoute = inject(ActivatedRoute);
  private store = inject(Store);

  walkingFarmId: string | null;
  propertyId: string | null;

  property$: Observable<WalkingFarmProperty>;

  alertLastDate$: Observable<Date | null>;
  alertsList$: Observable<WalkingFarmAlert[]>;
  alertsLoaded$: Observable<boolean>;
  unreadAlerts$: Observable<WalkingFarmAlert[]>;

  defaultBackHref: string | undefined;
  defaultHref: string;

  private unreadAlerts: WalkingFarmUnreadAlert[] = [];

  constructor() {
    this.walkingFarmId = this.activatedRoute.snapshot.paramMap.get('id');
    this.propertyId = this.activatedRoute.snapshot.paramMap.get('propertyId');

    if (this.walkingFarmId && this.propertyId) {
      this.store.dispatch(
        fromWalkingFarmActions.loadPropertyAlerts({
          payload: {
            id: this.walkingFarmId,
            propertyId: this.propertyId
          }
        })
      );
    }

    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];
    this.defaultHref = [
      this.defaultBackHref ?? '',
      'walking-farm',
      this.walkingFarmId,
      'view',
      'property',
      this.propertyId
    ].join('/');

    this.property$ = of(this.walkingFarmId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarm(id)))),
      filter((walkingFarm) => !isUndefined(walkingFarm)),
      map((walkingFarm) =>
        find(walkingFarm.properties, (property) => `${property.A000_PropertyNumber}` === this.propertyId)
      ),
      filter((walkingFarmProperty) => !isUndefined(walkingFarmProperty))
    );
    this.alertLastDate$ = this.store.pipe(select(fromWalkingFarmSelectors.selectPropertyAlertsAlertLastDate));
    this.alertsList$ = this.store.pipe(select(fromWalkingFarmSelectors.selectPropertyAlertsList));
    this.alertsLoaded$ = this.store.pipe(select(fromWalkingFarmSelectors.selectPropertyAlertsLoaded));
    this.unreadAlerts$ = this.store.pipe(select(fromWalkingFarmSelectors.selectUnreadPropertyAlertsList)).pipe(
      tap((unreadAlerts) => {
        this.unreadAlerts = lodashMap(unreadAlerts, (alert) => ({
          alertId: alert.id,
          farmId: this.walkingFarmId,
          propertyId: this.propertyId
        }));
      })
    );
  }

  alertsListTrackBy(_index: number, alert: WalkingFarmAlert) {
    return alert.id;
  }

  markAlertAsRead(alert: WalkingFarmAlert) {
    if (!alert.isRead && this.walkingFarmId) {
      this.store.dispatch(
        fromWalkingFarmActions.markAlertsAsRead({
          payload: {
            farmId: this.walkingFarmId,
            unreadAlerts: [
              {
                alertId: alert.id,
                propertyId: `${alert.propertyNumber}`
              }
            ]
          }
        })
      );
    }
  }

  markAllAlertsAsRead() {
    if (this.unreadAlerts.length && this.walkingFarmId) {
      this.store.dispatch(
        fromWalkingFarmActions.markAlertsAsRead({
          payload: {
            farmId: this.walkingFarmId,
            unreadAlerts: this.unreadAlerts
          }
        })
      );
    }
  }

  protected readonly addressType = AddressEnum;
}
